import { useCallback, useEffect, useReducer, useRef } from "react";
import type { CmmdTaskConversation } from "@cmmd-forge/contracts";

import { createCmmdApiClient } from "~/environments/cmmdApi";
import { sanitizePublicUiError } from "~/productSafeLabels";
import {
  initialTaskConversationHistoryState,
  taskConversationHistoryReducer,
} from "./taskConversationHistoryState";

const api = createCmmdApiClient();
const CONVERSATION_PAGE_SIZE = 20;
const EVENT_PAGE_SIZE = 50;

export function useTaskConversationHistory(taskId: number | null) {
  const [state, dispatch] = useReducer(
    taskConversationHistoryReducer,
    taskId,
    initialTaskConversationHistoryState,
  );
  const scopeRef = useRef(taskId);
  const listRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  scopeRef.current = taskId;

  useEffect(() => {
    listRequestRef.current += 1;
    detailRequestRef.current += 1;
    dispatch({ type: "reset", taskId });
  }, [taskId]);

  const visibleState =
    state.taskId === taskId ? state : initialTaskConversationHistoryState(taskId);

  const loadConversationPage = useCallback(
    async (cursor: string | null, append: boolean) => {
      if (taskId === null) return;
      const requestId = ++listRequestRef.current;
      dispatch({ type: "list-start", append });
      try {
        const result = await api.listTaskConversations(taskId, {
          limit: CONVERSATION_PAGE_SIZE,
          ...(cursor === null ? {} : { cursor }),
        });
        if (scopeRef.current !== taskId || listRequestRef.current !== requestId) return;
        dispatch({
          type: "list-ok",
          append,
          conversations: result.items,
          nextCursor: result.nextCursor,
        });
      } catch (cause) {
        if (scopeRef.current !== taskId || listRequestRef.current !== requestId) return;
        dispatch({
          type: "list-error",
          message: sanitizePublicUiError(cause, "Conversation history is unavailable."),
        });
      }
    },
    [taskId],
  );

  const ensureLoaded = useCallback(async () => {
    if (visibleState.loaded || visibleState.loading) return;
    await loadConversationPage(null, false);
  }, [loadConversationPage, visibleState.loaded, visibleState.loading]);

  const reload = useCallback(async () => {
    await loadConversationPage(null, false);
  }, [loadConversationPage]);

  const loadMoreConversations = useCallback(async () => {
    if (visibleState.conversationsNextCursor === null || visibleState.loadingMoreConversations) {
      return;
    }
    await loadConversationPage(visibleState.conversationsNextCursor, true);
  }, [loadConversationPage, visibleState]);

  const loadConversation = useCallback(
    async (conversationId: string) => {
      if (taskId === null) return;
      const requestId = ++detailRequestRef.current;
      dispatch({ type: "detail-start", conversationId, append: false });
      try {
        const conversation = await api.getTaskConversation(taskId, conversationId, {
          limit: EVENT_PAGE_SIZE,
        });
        if (scopeRef.current !== taskId || detailRequestRef.current !== requestId) return;
        dispatch({ type: "detail-ok", conversation, append: false });
      } catch (cause) {
        if (scopeRef.current !== taskId || detailRequestRef.current !== requestId) return;
        dispatch({
          type: "detail-error",
          message: sanitizePublicUiError(cause, "Could not load this conversation."),
        });
      }
    },
    [taskId],
  );

  const loadMoreEvents = useCallback(async () => {
    const pageRequest = getOlderEventPageRequest(
      taskId,
      visibleState.selectedConversation,
      visibleState.loadingMoreEvents,
    );
    if (pageRequest === null) return;
    const requestId = ++detailRequestRef.current;
    dispatch({
      type: "detail-start",
      conversationId: pageRequest.conversation.conversationId,
      append: true,
    });
    try {
      const conversation = await api.getTaskConversation(
        pageRequest.taskId,
        pageRequest.conversation.conversationId,
        {
          limit: EVENT_PAGE_SIZE,
          cursor: pageRequest.cursor,
        },
      );
      if (scopeRef.current !== pageRequest.taskId || detailRequestRef.current !== requestId) return;
      dispatch({ type: "detail-ok", conversation, append: true });
    } catch (cause) {
      if (scopeRef.current !== pageRequest.taskId || detailRequestRef.current !== requestId) return;
      dispatch({
        type: "detail-error",
        message: sanitizePublicUiError(cause, "Could not load more messages."),
      });
    }
  }, [taskId, visibleState]);

  const clearConversation = useCallback(() => {
    detailRequestRef.current += 1;
    dispatch({ type: "detail-clear" });
  }, []);

  return {
    conversations: visibleState.conversations,
    conversationsNextCursor: visibleState.conversationsNextCursor,
    selectedConversation: visibleState.selectedConversation,
    expandedConversationId: visibleState.expandedConversationId,
    conversationsLoaded: visibleState.loaded,
    conversationsLoading: visibleState.loading,
    loadingMoreConversations: visibleState.loadingMoreConversations,
    loadingConversationId: visibleState.loadingConversationId,
    loadingMoreConversationEvents: visibleState.loadingMoreEvents,
    conversationsError: visibleState.error,
    conversationDetailError: visibleState.detailError,
    ensureConversationsLoaded: ensureLoaded,
    reloadConversations: reload,
    loadMoreConversations,
    loadConversation,
    loadMoreConversationEvents: loadMoreEvents,
    clearConversation,
  };
}

function getOlderEventPageRequest(
  taskId: number | null,
  selected: CmmdTaskConversation | null,
  loading: boolean,
): {
  readonly taskId: number;
  readonly conversation: CmmdTaskConversation;
  readonly cursor: string;
} | null {
  if (taskId === null || selected === null || loading) return null;
  return selected.nextCursor === null
    ? null
    : { taskId, conversation: selected, cursor: selected.nextCursor };
}
